import {useDraggable} from '@dnd-kit/core';
import {CSS} from '@dnd-kit/utilities';
import {useComponentsTheme} from 'platform/components';
import {getSize, Icon, Show, Text} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {CSSProperties} from 'react';

import {pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CSS_ALL_DAY_EVENT_HEIGHT} from '../constants/cssAllDayEventHeight';
import {EVENT_FILTER_WHEN_IS_PAST} from '../constants/eventFilterWhenIsPast';
import {BigCalendarEvent} from '../types/BigCalendarEvent';
import {formatEventTimeSpan} from '../utils/formatEventTimeSpan';
import {isMultiDayEvent} from '../utils/isMultiDayEvent';
import {isPastEvent} from '../utils/isPastEvent';
import {repeatingGradient} from '../utils/repeatingGradient';
import {resolveCalendarColor} from '../utils/resolveCalendarColor';

interface AllDayEventItemProps extends RequiredTestIdProps {
  event: BigCalendarEvent;
  /** The event started before the visible range, so the left edge stays square. */
  isStartClipped?: boolean;
  /** The event ends after the visible range, so the right edge stays square. */
  isEndClipped?: boolean;
  /**
   * Title-only chip sized to its content, for a header where chips wrap in rows. Clipped sides
   * are marked with a darker inner edge instead of an arrow.
   */
  isCompact?: boolean;
  onClick?: (event: BigCalendarEvent) => void;
  enableHover?: boolean;
  onMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onMouseLeave?: (event: BigCalendarEvent) => void;
}

type AllDayEventStyle = CSSProperties & {
  '--event-filter': string;
};

const allDayEventVariants = cva(
  'pointer-events-auto flex min-w-0 cursor-pointer items-center [transform-origin:0_0] [transition:filter_0.2s_ease] [will-change:transform]',
  {
    variants: {
      enableHover: {
        true: 'hover:[filter:brightness(0.85)_var(--event-filter)]',
        false: '',
      },
    },
  }
);

/**
 * One event in the all-day header of the week and day views. Drawn as a flat bar in the event
 * colour, matching the month view, so a multi-day event reads the same in every view.
 */
export function AllDayEventItem(props: AllDayEventItemProps) {
  const theme = useComponentsTheme();
  const isMultiDay = isMultiDayEvent(props.event.start, props.event.end);
  const showsTimeSpan = !props.isCompact && isMultiDay && !props.event.isAllDay;
  const showsArrows = !props.isCompact;
  const isPast = isPastEvent(props.event);

  const {attributes, listeners, setNodeRef, transform} = useDraggable({
    id: props.event.id,
    disabled: props.event.isReadOnly,
  });

  const onClick = () => {
    if (props.event.isReadOnly) {
      return;
    }

    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_all_day_event_click',
      value: {
        eventId: props.event.id,
      },
    });

    props.onClick?.(props.event);
  };

  const onMouseEnter = (mouseEvent: React.MouseEvent) => {
    props.onMouseEnter?.(props.event, mouseEvent);
  };

  const onMouseLeave = () => {
    props.onMouseLeave?.(props.event);
  };

  const eventColorHEX = resolveCalendarColor(props.event.color || 'general.black', theme);
  const textColor = props.event.alternative ? 'primary' : 'white';

  const leftRadius = props.isStartClipped ? 0 : theme.radii.small;
  const rightRadius = props.isEndClipped ? 0 : theme.radii.small;

  const clippedEdgeShadow = match({
    isCompact: props.isCompact === true,
    isStartClipped: props.isStartClipped === true,
    isEndClipped: props.isEndClipped === true,
  })
    .with({isCompact: true, isStartClipped: true, isEndClipped: true}, () =>
      [CLIPPED_START_SHADOW, CLIPPED_END_SHADOW].join(', ')
    )
    .with({isCompact: true, isStartClipped: true}, () => CLIPPED_START_SHADOW)
    .with({isCompact: true, isEndClipped: true}, () => CLIPPED_END_SHADOW)
    .otherwise(() => 'none');

  const eventFilter = isPast ? EVENT_FILTER_WHEN_IS_PAST : 'none';
  const style: AllDayEventStyle = {
    '--event-filter': eventFilter,
    gap: getSize(1),
    width: props.isCompact ? 'auto' : '100%',
    height: CSS_ALL_DAY_EVENT_HEIGHT,
    padding: `0 ${getSize(2)}`,
    borderRadius: `${leftRadius} ${rightRadius} ${rightRadius} ${leftRadius}`,
    backgroundColor: props.event.alternative ? 'transparent' : eventColorHEX,
    backgroundImage: props.event.alternative ? repeatingGradient(eventColorHEX) : 'none',
    border: props.event.alternative ? `1px solid ${eventColorHEX}` : 'none',
    boxShadow: clippedEdgeShadow,
    transform: CSS.Transform.toString(transform),
    transformOrigin: '0 0',
    willChange: 'transform',
    filter: eventFilter,
    transition: 'filter 0.2s ease',
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(allDayEventVariants({enableHover: props.enableHover}))}
      style={style}
      data-testid={suffixTestId('allDayEventItem', props)}
    >
      <Show when={showsArrows && props.isStartClipped}>
        <span data-testid={suffixTestId('allDayEventItem-continuesBefore', props)}>
          <Icon value="navigation/chevron_left" size={3} color={ARROW_COLOR[textColor]} />
        </span>
      </Show>
      <Show when={props.event.icon}>
        <Icon
          data-testid={suffixTestId('allDayEventItem-icon', props)}
          value={props.event.icon}
          size={3}
          color={ARROW_COLOR[textColor]}
        />
      </Show>
      <Text
        data-testid={suffixTestId('allDayEventItem-title', props)}
        size="xSmall"
        color={textColor}
        alternative
        noWrap
      >
        {props.event.title}
      </Text>
      <Show when={showsTimeSpan}>
        <Text
          data-testid={suffixTestId('allDayEventItem-span', props)}
          size="xSmall"
          color={textColor}
          noWrap
        >
          {formatEventTimeSpan(props.event)}
        </Text>
      </Show>
      <Show when={showsArrows && props.isEndClipped}>
        <span
          data-testid={suffixTestId('allDayEventItem-continuesAfter', props)}
          className="ml-auto"
        >
          <Icon value="navigation/chevron_right" size={3} color={ARROW_COLOR[textColor]} />
        </span>
      </Show>
    </div>
  );
}

const ARROW_COLOR = {
  primary: 'general.black',
  white: 'general.white',
} as const;

/** Darker inner edge marking a clipped side of a compact chip; works on any event colour. */
const CLIPPED_EDGE_COLOR = 'rgba(0, 0, 0, 0.25)';
const CLIPPED_EDGE_WIDTH = '3px';
const CLIPPED_START_SHADOW = `inset ${CLIPPED_EDGE_WIDTH} 0 0 ${CLIPPED_EDGE_COLOR}`;
const CLIPPED_END_SHADOW = `inset -${CLIPPED_EDGE_WIDTH} 0 0 ${CLIPPED_EDGE_COLOR}`;
